import axios, { HttpStatusCode } from "axios";
import React, { useEffect, useState } from "react";
import { BASE_URL } from "../../Constants";
import WeeklyHoursTable from "./WeeklyHourTable";
import {
  convertToDDMMYYYY,
  getCurrentWeekNumber,
  getWeekRanges,
} from "../../utensils/dateUtils";
import MultiselectDropdown from "./MultiSelectDropdown";
import ProgressLoader from "../ProgressLoader/ProgressLoader";

function ResourceUtilization() {
  const [isLoading, setIsLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [startDate, setStartDate] = useState([]);
  const [endDate, setEndDate] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterProject, setFilterProject] = useState([]);
  const [filterUsers, setFilterUsers] = useState([]); // New state for selected users
  const [allUsers, setAllUsers] = useState([]);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [selectedWeek, setSelectedWeek] = useState(
    getCurrentWeekNumber() - 1 === 0 ? 1 : getCurrentWeekNumber() - 1
  );
  const [apiData, setApiData] = useState();
  const weeks = getWeekRanges(currentYear);

  const fetchAllUsers = () => {
    axios
      .get("https://timesheet-be.fleetstudio.com/api/user/getUsers")
      .then((response) => {
        const users = response.data; // Assuming response.data contains the array of users
        const filteredUsers = users.map((user) => ({
          _id: user._id,
          firstName: user.fname,
          lastName: user.lname,
        }));
        setAllUsers(filteredUsers);
        setFilterUsers(["all"]); // Default to select all users
      })
      .catch((error) => {
        console.error(
          "Error fetching users:",
          error.response ? error.response.data : error.message
        );
      });
  };

  const handleWeekChange = (e) => {
    setSelectedWeek(e.target.value);
  };

  useEffect(() => {
    fetchAllUsers();
    fetchProjects();
  }, []);

  useEffect(() => {
    const startDate = weeks.find(
      (w) => w.week === parseInt(selectedWeek)
    )?.start;
    const endDate = weeks.find((w) => w.week === parseInt(selectedWeek))?.end;
    setStartDate(startDate);
    setEndDate(endDate);
  }, [selectedWeek]);

  const fetchProjects = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/api/user/user/${localStorage.getItem(
          "uid"
        )}/filterProjects/role/Project%20Manager`
      );
      if (response.status === HttpStatusCode.Ok) {
        setIsLoading(false);
      }
      setProjects(response.data?.uniqueProjects);
      setUsers(response.data?.uniqueUsers);
    } catch (error) {
      setIsLoading(false);
      alert("Something went wrong");
      console.error("Error fetching projects:", error);
    }
  };

  const regenerateNodes = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/api/user/refreshNodes/${startDate}/${endDate}`
      );
      if (response.status === HttpStatusCode.Ok) {
        setIsLoading(false);
        setApiData(response.data);
      }
    } catch (error) {
      setIsLoading(false);
      alert("Something went wrong");
      console.error("Error fetching projects:", error);
    }
  };
  const fetchReports = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/api/user/reports/filter/${startDate}/${endDate}`
      );
      if (response.status === HttpStatusCode.Ok) {
        setIsLoading(false);
        setApiData(response.data);
      }
    } catch (error) {
      setIsLoading(false);
      alert("Something went wrong");
      console.error("Error fetching reports:", error);
    }
  };

  return (
    <div>
      <div className="container mx-auto p-4">
        <h1 className="text-3xl font-bold mb-4">Timesheet Logs</h1>
        <div className="bg-white shadow-md p-4 mb-6 rounded">
          <h2 className="text-xl font-semibold mb-2">Filter Timesheets</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Project Multi-select Dropdown */}
            <MultiselectDropdown
              projects={projects}
              filterProject={filterProject}
              setFilterProject={setFilterProject}
            />

            {/* User Multi-select Dropdown */}
            <div>
              <label className="block font-medium mb-2">
                Select Users <sup className="text-red-600">*</sup>
              </label>
              <select
                multiple
                value={filterUsers}
                onChange={(e) =>
                  setFilterUsers(
                    Array.from(
                      e.target.selectedOptions,
                      (option) => option.value
                    )
                  )
                }
                className="w-full p-2 border rounded"
              >
                <option value="all">All Users</option>
                {allUsers
                  .sort((a, b) => {
                    const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
                    const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
                    return nameA.localeCompare(nameB);
                  })
                  .map((user) => (
                    <option key={user._id} value={user._id}>
                      {user.firstName} {user.lastName}
                    </option>
                  ))}
              </select>
            </div>

            {/* Week Selector */}
            <div className="space-y-4 w-full">
              <div className="flex items-center  ">
                {/* Previous Year Button */}
                {currentYear >= 2024 && (
                  <button
                    onClick={() => setCurrentYear((year) => year - 1)}
                    className="px-4 py-2 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Previous Year
                  </button>
                )}

                {/* Label */}
                <label
                  htmlFor="week-dropdown"
                  className="block text-sm font-medium text-gray-600 mx-5 px-5"
                >
                  Select Week
                </label>

                {/* Next Year Button (conditionally hidden) */}
                {currentYear < new Date().getFullYear() && (
                  <button
                    onClick={() => setCurrentYear((year) => year + 1)}
                    className="px-4 py-2 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Next Year
                  </button>
                )}
              </div>

              {/* Dropdown */}
              <select
                id="week-dropdown"
                value={selectedWeek}
                onChange={handleWeekChange}
                className="block w-full px-4 py-2 text-sm border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="" disabled>
                  -- Select a Week --
                </option>
                {weeks.map((week) => (
                  <option key={week.week} value={week.week}>
                    Week {week.week}: ({week?.start} - {week?.end})
                  </option>
                ))}
              </select>
            </div>

            {/* Action Buttons */}
            <div className="col-span-1 md:col-span-4 flex justify-end gap-6">
              {/* <button
                onClick={regenerateNodes}
                className="bg-red-500 text-white px-4 py-2 rounded mt-4"
              >
                Regenerate Nodes
              </button> */}
              <button
                onClick={fetchReports}
                className="bg-blue-500 text-white px-4 py-2 rounded  "
                disabled={isLoading}
              >
                {isLoading ? "Loading..." : "Fetch Timesheets"}
              </button>
            </div>
          </div>
        </div>
        {apiData && (
          <WeeklyHoursTable
            data={apiData}
            filteredProjectId={filterProject}
            filteredUsers={filterUsers}
            startDate={convertToDDMMYYYY(startDate)}
            endDate={convertToDDMMYYYY(endDate)}
            refetch={fetchReports}
          />
        )}
        {isLoading && <ProgressLoader />}
      </div>
    </div>
  );
}

export default ResourceUtilization;
